import React from "react";
import { Box, Typography, Button, Paper, Chip } from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PetsIcon from "@mui/icons-material/Pets";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

interface BookingContextBannerProps {
  bookingId: string;
  dates: string;
  petInfo: string;
  price: number;
  status?: "pending" | "confirmed" | "completed";
  onViewDetails: (bookingId: string) => void;
}

const BookingContextBanner: React.FC<BookingContextBannerProps> = ({
  bookingId,
  dates,
  petInfo,
  price,
  status = "pending",
  onViewDetails,
}) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "#FFA726";
      case "confirmed":
        return "#66BB6A";
      case "completed":
        return "#9E9E9E";
      default:
        return "#9E9E9E";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "pending":
        return "Pending";
      case "confirmed":
        return "Confirmed";
      case "completed":
        return "Completed";
      default:
        return "Unknown";
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mx: 3,
        mt: 2,
        mb: 2,
        p: 2,
        backgroundColor: "#FFF8E1",
        border: "1px solid #FFE082",
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Pending Booking Request:
            </Typography>
            <Chip
              label={getStatusLabel(status)}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.7rem",
                fontWeight: 700,
                backgroundColor: getStatusColor(status),
                color: "white",
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <CalendarTodayIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {dates}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              •
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <PetsIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {petInfo}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              •
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <AttachMoneyIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                ${price}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Button
          variant="contained"
          size="small"
          onClick={() => onViewDetails(bookingId)}
          sx={{
            backgroundColor: "#FF6B35",
            color: "white",
            borderRadius: 25,
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            "&:hover": {
              backgroundColor: "#E55A2B",
            },
          }}
        >
          View Details
        </Button>
      </Box>
    </Paper>
  );
};

export default BookingContextBanner;
